import React,{useCallback,useEffect,useMemo,useState} from 'react';
import {supabase} from '../../supabase/supabase';
import SafeIcon from '../../common/SafeIcon';
import DisputeEvidencePanel from '../orders/DisputeEvidencePanel';
import * as FiIcons from 'react-icons/fi';

const {
  FiAlertTriangle,
  FiCheckCircle,
  FiClock,
  FiDollarSign,
  FiLoader,
  FiRefreshCw,
  FiSearch,
  FiXCircle
}=FiIcons;

const statusStyles={
  open:'bg-red-50 text-red-600',
  investigating:'bg-amber-50 text-amber-700',
  resolved:'bg-emerald-50 text-emerald-700',
  rejected:'bg-slate-100 text-slate-600'
};

const getFunctionErrorMessage=async(error)=>{
  if (!error) return 'The Stripe dispute request failed.';

  if (error.context && typeof error.context.json==='function') {
    try {
      const payload=await error.context.json();

      if (payload?.error) {
        return payload.error;
      }
    } catch {
      // Fall back to the SDK message if the response was not JSON.
    }
  }

  if (error.context instanceof Error && error.context.message) {
    return error.context.message;
  }

  return error.message || 'The Stripe dispute request failed.';
};

const AdminOrderDisputesPanel=()=>{
  const [disputes,setDisputes]=useState([]);
  const [financialActions,setFinancialActions]=useState([]);
  const [status,setStatus]=useState('all');
  const [search,setSearch]=useState('');
  const [loading,setLoading]=useState(true);
  const [savingId,setSavingId]=useState(null);
  const [error,setError]=useState('');

  const loadDisputes=useCallback(async()=>{
    setLoading(true);
    setError('');

    const [disputeResult,actionResult]=await Promise.all([
      supabase.rpc('admin_get_order_disputes_with_refunds_1793052000000'),
      supabase.rpc('admin_get_order_dispute_financial_actions_1793053000000')
    ]);

    if (disputeResult.error) {
      setError(disputeResult.error.message);
    } else {
      setDisputes(disputeResult.data || []);
    }

    if (actionResult.error) {
      setFinancialActions([]);
      setError((previous)=>previous || actionResult.error.message);
    } else {
      setFinancialActions(actionResult.data || []);
    }

    setLoading(false);
  },[]);

  useEffect(()=>{
    loadDisputes();
  },[loadDisputes]);

  const visibleDisputes=useMemo(()=>{
    const query=search.trim().toLowerCase();

    return disputes.filter((dispute)=>{
      const matchesStatus=status==='all' || dispute.dispute_status===status;
      const matchesSearch=!query || [
        dispute.order_id,
        dispute.reporter_name,
        dispute.reporter_email,
        dispute.buyer_name,
        dispute.seller_business_name,
        dispute.reason,
        dispute.description
      ].some((value)=>String(value || '').toLowerCase().includes(query));

      return matchesStatus && matchesSearch;
    });
  },[disputes,search,status]);

  const resolveDispute=async(dispute,nextStatus)=>{
    const resolutionNote=window.prompt(
      nextStatus==='rejected'
        ? 'Explain why this dispute is being rejected.'
        : 'Explain the resolution provided to the buyer and seller.'
    );

    if (!resolutionNote || resolutionNote.trim().length<10) {
      return;
    }

    let refundAmount=0;

    if (nextStatus==='resolved' && dispute.requested_resolution!=='replacement') {
      const input=window.prompt(
        `Refund amount in CAD (0 for no refund, maximum ${Number(dispute.order_total || 0).toFixed(2)}):`,
        dispute.requested_resolution==='full_refund'
          ? Number(dispute.order_total || 0).toFixed(2)
          : '0.00'
      );

      if (input===null) return;

      refundAmount=Number(input);

      if (!Number.isFinite(refundAmount) || refundAmount<0) {
        setError('Enter a valid refund amount.');
        return;
      }
    }

    setSavingId(dispute.dispute_id);
    setError('');

    try {
      const {data,error:invokeError}=await supabase.functions.invoke(
        'stripe-order-dispute',
        {
          body:{
            dispute_id:dispute.dispute_id,
            status:nextStatus,
            resolution_note:resolutionNote.trim(),
            refund_amount:refundAmount
          }
        }
      );

      if (invokeError) throw invokeError;
      if (data?.error) throw new Error(data.error);

      await loadDisputes();
    } catch (requestError) {
      setError(await getFunctionErrorMessage(requestError));
    } finally {
      setSavingId(null);
    }
  };

  const runFinancialAction=async(dispute,actionType)=>{
    const orderRefunded=Math.max(
      Number(dispute.order_refund_amount || 0),
      Number(dispute.refund_amount || 0)
    );
    const priorReversals=financialActions
      .filter((action)=>(
        action.order_id===dispute.order_id
        && action.action_type==='transfer_reversal'
        && action.status==='succeeded'
      ))
      .reduce((total,action)=>total+Number(action.amount_cents || 0),0)/100;
    const maximum=actionType==='additional_refund'
      ? Math.max(Number(dispute.order_total || 0)-orderRefunded,0)
      : Math.max(orderRefunded-priorReversals,0);

    if (maximum<=0) {
      setError(
        actionType==='additional_refund'
          ? 'This order has no remaining refundable balance.'
          : 'There is no unreversed refund amount available to charge back to the seller.'
      );
      return;
    }

    const prompt=actionType==='additional_refund'
      ? `Additional refund amount in CAD (maximum ${maximum.toFixed(2)}):`
      : `Seller transfer reversal in CAD (maximum ${maximum.toFixed(2)}). This reverses the seller's Stripe transfer; it does not create another customer refund.`;
    const input=window.prompt(prompt,maximum.toFixed(2));

    if (input===null) return;

    const amount=Number(input);

    if (!Number.isFinite(amount) || amount<=0 || amount>maximum) {
      setError(`Enter an amount greater than zero and no more than ${maximum.toFixed(2)}.`);
      return;
    }

    setSavingId(dispute.dispute_id);
    setError('');

    try {
      const {data,error:invokeError}=await supabase.functions.invoke(
        'stripe-order-dispute',
        {
          body:{
            action:actionType,
            dispute_id:dispute.dispute_id,
            action_key:crypto.randomUUID(),
            amount_cents:Math.round(amount*100)
          }
        }
      );

      if (invokeError) throw invokeError;
      if (data?.error) throw new Error(data.error);

      await loadDisputes();
    } catch (requestError) {
      setError(await getFunctionErrorMessage(requestError));
    } finally {
      setSavingId(null);
    }
  };

  const counts={
    open:disputes.filter((dispute)=>dispute.dispute_status==='open').length,
    investigating:disputes.filter((dispute)=>dispute.dispute_status==='investigating').length,
    resolved:disputes.filter((dispute)=>dispute.dispute_status==='resolved').length
  };

  return (
    <section className="mt-8 space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <SummaryCard icon={FiAlertTriangle} label="Open disputes" value={counts.open} tone="red" />
        <SummaryCard icon={FiClock} label="Investigating" value={counts.investigating} tone="amber" />
        <SummaryCard icon={FiCheckCircle} label="Resolved" value={counts.resolved} tone="emerald" />
      </div>

      <div className="overflow-hidden rounded-[2.5rem] border border-gray-100 bg-white shadow-sm">
        <div className="space-y-5 border-b border-gray-50 p-8">
          <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
            <div>
              <h2 className="text-xl font-black text-gray-900">Order Dispute Resolution</h2>
              <p className="mt-1 text-[10px] font-black uppercase tracking-widest text-slate-400">
                Review buyer and seller issues, then issue a controlled resolution
              </p>
            </div>
            <button
              type="button"
              onClick={loadDisputes}
              className="flex items-center justify-center gap-2 rounded-xl bg-indigo-50 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-indigo-600"
            >
              <SafeIcon icon={FiRefreshCw} />
              Refresh
            </button>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(event)=>setSearch(event.target.value)}
                placeholder="Search order, buyer, seller, or reason..."
                className="w-full rounded-xl bg-gray-50 py-3 pl-11 pr-4 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <select
              value={status}
              onChange={(event)=>setStatus(event.target.value)}
              className="rounded-xl bg-gray-50 px-4 py-3 text-[10px] font-black uppercase tracking-widest outline-none"
            >
              <option value="all">All statuses</option>
              <option value="open">Open</option>
              <option value="investigating">Investigating</option>
              <option value="resolved">Resolved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="mx-8 mt-6 rounded-2xl bg-red-50 p-4 text-sm font-bold text-red-700">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px]">
            <thead>
              <tr className="bg-gray-50/50 text-left text-[10px] font-black uppercase tracking-widest text-gray-400">
                <th className="px-8 py-4">Dispute</th>
                <th className="px-8 py-4">Order / Parties</th>
                <th className="px-8 py-4">Description</th>
                <th className="px-8 py-4">Status</th>
                <th className="px-8 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr>
                  <td colSpan="5" className="py-20 text-center">
                    <SafeIcon icon={FiLoader} className="mx-auto animate-spin text-3xl text-indigo-300" />
                  </td>
                </tr>
              ) : visibleDisputes.length===0 ? (
                <tr>
                  <td colSpan="5" className="py-20 text-center text-[10px] font-black uppercase tracking-widest text-gray-400">
                    No order disputes found
                  </td>
                </tr>
              ) : visibleDisputes.map((dispute)=>(
                <tr key={dispute.dispute_id} className="align-top">
                  <td className="px-8 py-5">
                    <p className="text-sm font-black capitalize text-gray-900">
                      {dispute.reason.replaceAll('_',' ')}
                    </p>
                    <p className="mt-1 text-[9px] font-bold uppercase text-slate-400">
                      {new Date(dispute.created_at).toLocaleString()}
                    </p>
                    <p className="mt-2 text-[10px] font-black uppercase tracking-widest text-indigo-500">
                      Requested: {dispute.requested_resolution.replaceAll('_',' ')}
                    </p>
                  </td>
                  <td className="px-8 py-5">
                    <p className="text-xs font-black text-indigo-600">
                      #{dispute.order_id.slice(0,8).toUpperCase()}
                    </p>
                    <p className="mt-2 text-xs font-bold text-gray-900">
                      {dispute.seller_business_name}
                    </p>
                    <p className="text-[10px] text-slate-400">
                      Buyer: {dispute.buyer_name}
                    </p>
                    <p className="mt-2 text-xs font-black text-slate-700">
                      ${Number(dispute.order_total || 0).toFixed(2)}
                    </p>
                  </td>
                  <td className="max-w-sm px-8 py-5">
                    <details>
                      <summary className="cursor-pointer text-[10px] font-black uppercase tracking-widest text-indigo-600">
                        View dispute details
                      </summary>
                      <div className="mt-3">
                        <p className="whitespace-pre-wrap text-sm text-slate-600">
                          {dispute.description || 'No description provided.'}
                        </p>
                        <DisputeEvidencePanel
                          disputeId={dispute.dispute_id}
                          orderId={dispute.order_id}
                          canUpload={false}
                        />
                        <p className="mt-2 text-[10px] text-slate-400">
                          Reported by {dispute.reporter_name}
                        </p>
                      </div>
                    </details>
                  </td>
                  <td className="px-8 py-5">
                    <span className={`rounded-full px-3 py-1 text-[9px] font-black uppercase tracking-widest ${statusStyles[dispute.dispute_status] || 'bg-gray-50 text-gray-600'}`}>
                      {dispute.dispute_status}
                    </span>
                    {['resolved','rejected'].includes(dispute.dispute_status) && (
                      <details className="mt-3">
                        <summary className="cursor-pointer text-[9px] font-black uppercase tracking-widest text-emerald-600">
                          Resolution result
                        </summary>
                        <div className="mt-3 space-y-1">
                          <p className={`text-xs font-black ${dispute.dispute_status==='resolved' ? 'text-emerald-600' : 'text-slate-700'}`}>
                            {dispute.dispute_status==='resolved' ? 'Resolved' : 'Rejected'}
                          </p>
                          <p className="text-xs font-black text-emerald-600">
                            {Number(dispute.refund_amount || dispute.order_refund_amount || 0)>0
                              ? `Refund: $${Number(dispute.refund_amount || dispute.order_refund_amount).toFixed(2)}`
                              : 'No refund issued'}
                          </p>
                          {dispute.stripe_refund_id && (
                            <p className="break-all text-[10px] text-slate-500">
                              Stripe refund: {dispute.stripe_refund_id}
                            </p>
                          )}
                          {dispute.resolution_note && (
                            <p className="max-w-xs whitespace-pre-wrap text-[10px] text-slate-500">
                              {dispute.resolution_note}
                            </p>
                          )}
                          <div className="mt-3 space-y-2">
                            {financialActions
                              .filter((action)=>action.dispute_id===dispute.dispute_id)
                              .map((action)=>(
                                <div key={action.id} className="rounded-lg bg-slate-50 p-2 text-left">
                                  <p className="text-[9px] font-black uppercase text-slate-600">
                                    {action.action_type==='additional_refund'
                                      ? 'Additional buyer refund'
                                      : 'Seller transfer reversal'}
                                    {' · '}
                                    {action.status}
                                  </p>
                                  <p className="text-[10px] font-bold text-slate-700">
                                    ${(Number(action.amount_cents || 0)/100).toFixed(2)}
                                    {action.stripe_status ? ` · Stripe ${action.stripe_status}` : ''}
                                  </p>
                                  {action.stripe_refund_id && (
                                    <p className="break-all text-[9px] text-slate-500">
                                      Refund: {action.stripe_refund_id}
                                    </p>
                                  )}
                                  {action.stripe_transfer_reversal_id && (
                                    <p className="break-all text-[9px] text-slate-500">
                                      Reversal: {action.stripe_transfer_reversal_id}
                                    </p>
                                  )}
                                  {action.error_message && (
                                    <p className="mt-1 break-words text-[9px] text-red-600">
                                      {action.error_message}
                                    </p>
                                  )}
                                </div>
                              ))}
                          </div>
                        </div>
                      </details>
                    )}
                  </td>
                  <td className="px-8 py-5 text-right">
                    <div className="flex flex-col items-end gap-2">
                      {dispute.dispute_status==='open' && (
                        <button
                          type="button"
                          disabled={savingId===dispute.dispute_id}
                          onClick={()=>resolveDispute(dispute,'investigating')}
                          className="rounded-xl bg-amber-100 px-3 py-2 text-[9px] font-black uppercase tracking-widest text-amber-700 disabled:opacity-50"
                        >
                          Investigate
                        </button>
                      )}
                      {['open','investigating'].includes(dispute.dispute_status) && (
                        <>
                          <button
                            type="button"
                            disabled={savingId===dispute.dispute_id}
                            onClick={()=>resolveDispute(dispute,'resolved')}
                            className="rounded-xl bg-emerald-600 px-3 py-2 text-[9px] font-black uppercase tracking-widest text-white disabled:opacity-50"
                          >
                            Resolve
                          </button>
                          <button
                            type="button"
                            disabled={savingId===dispute.dispute_id}
                            onClick={()=>resolveDispute(dispute,'rejected')}
                            className="rounded-xl bg-slate-100 px-3 py-2 text-[9px] font-black uppercase tracking-widest text-slate-600 disabled:opacity-50"
                          >
                            Reject
                          </button>
                        </>
                      )}
                      {dispute.dispute_status==='resolved' && (
                        <>
                          <button
                            type="button"
                            disabled={savingId===dispute.dispute_id}
                            onClick={()=>runFinancialAction(dispute,'additional_refund')}
                            className="flex items-center gap-1 rounded-xl bg-emerald-600 px-3 py-2 text-[9px] font-black uppercase tracking-widest text-white disabled:opacity-50"
                          >
                            <SafeIcon icon={FiDollarSign} />
                            Refund more
                          </button>
                          <button
                            type="button"
                            disabled={savingId===dispute.dispute_id}
                            onClick={()=>runFinancialAction(dispute,'transfer_reversal')}
                            className="rounded-xl bg-amber-100 px-3 py-2 text-[9px] font-black uppercase tracking-widest text-amber-800 disabled:opacity-50"
                          >
                            Charge seller
                          </button>
                        </>
                      )}
                      {savingId===dispute.dispute_id && (
                        <SafeIcon icon={FiLoader} className="animate-spin text-indigo-500" />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

const SummaryCard=({icon,label,value,tone})=>(
  <div className="flex items-center gap-4 rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
    <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${
      tone==='red'
        ? 'bg-red-50 text-red-600'
        : tone==='amber'
          ? 'bg-amber-50 text-amber-600'
          : 'bg-emerald-50 text-emerald-600'
    }`}>
      <SafeIcon icon={icon} />
    </div>
    <div>
      <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">{label}</p>
      <p className="text-2xl font-black text-gray-900">{value}</p>
    </div>
  </div>
);

export default AdminOrderDisputesPanel;
